'use client';

import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import type { RefferalProgramApi } from '@/api/cms';
import { Button } from '@/components/buttons';
import { Modals } from '@/helpers/modals';
import { showTooltip } from '@/helpers/tooltip';
import { useNavigation } from '@/hooks/navigation';
import { FAQSection } from '@/modules/common/components';
import { useInfoProject } from '@/modules/common/hooks';
import { useGetCommonStore, useModalsStore } from '@/store';
import { TooltipTypes } from '@/store/tooltipStore';
import { ScContainer } from '@/styles/common';
import { HowToParticipateSection } from './HowToParticipate';

import { ScBonusCard, ScReferalProgramPage, ScReferralHeader } from './styled';
import { TopSection } from './TopSectionBlock';

interface IReferralProgram {
  data: RefferalProgramApi;
}

export const ReferralProgram: FC<IReferralProgram> = ({
  data: referralProgramData,
}) => {
  const { t } = useTranslation(['common', 'referralSystem']);
  const useCommonStore = useGetCommonStore();

  const { isAuthenticated } = useCommonStore(
    useShallow((state) => ({
      isAuthenticated: state.isAuthenticated,
    }))
  );
  const { data: project } = useInfoProject();

  const open = useModalsStore.use.open();
  const { navigate } = useNavigation();

  const handleClickOnBonusCard = () => {
    if (!isAuthenticated) {
      return open(Modals.Registration);
    }

    if (!project?.availableReferralSystem) {
      return showTooltip({
        type: TooltipTypes.WARNING,
        body: (
          <div>
            <p className="title">
              {t('referralSystem:notAvailableTooltip.title')}
            </p>
            <p>{t('referralSystem:notAvailableTooltip.description')}</p>
          </div>
        ),
      });
    }

    navigate('/profile-custom/referral-system');
  };

  const {
    title,
    description,
    bonusTitle,
    referralBonus,
    bonusSubtitle,
    howToTitle,
    cards,
    questions,
    ...images
  } = referralProgramData;
  return (
    <ScReferalProgramPage>
      <TopSection
        bg={images?.topBg?.url || ''}
        leftDecorImg={images?.lupImg?.url || ''}
        leftImg={images?.leftImg?.url || ''}
        mobImg={images?.mobImg?.url || ''}
        rightDecorImg={images?.onePlusImg?.url || ''}
        rightImg={images?.rightImg?.url || ''}
      >
        <ScReferralHeader>
          <div className="header-text">{title}</div>
          <div
            className="header-intro"
            dangerouslySetInnerHTML={{ __html: description }}
          />
          <ScBonusCard>
            <div className="card-content">
              <div className="card-text">{bonusTitle}</div>
              <div className="bonus">{referralBonus}</div>
              <div className="card-friend">{bonusSubtitle}</div>
            </div>
            <Button onClick={handleClickOnBonusCard} primary small wide>
              {isAuthenticated
                ? t('referralProgram.button.link')
                : t('button.register')}
            </Button>
          </ScBonusCard>
        </ScReferralHeader>
      </TopSection>

      <ScContainer>
        <div className="content">
          <HowToParticipateSection cards={cards} title={howToTitle} />
          <FAQSection items={questions} />
        </div>
      </ScContainer>
    </ScReferalProgramPage>
  );
};
